import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { ApiError } from '@formosa/shared/api/request';
import PasswordField from '../../components/security/PasswordField';
import SettingsSubPageShell from '../../components/settings/SettingsSubPageShell';
import { useTranslation } from '../../i18n/LocaleContext';

const VERIFY_SCENE = 'RESET_TRADE_PASSWORD';

export default function WithdrawPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifyTab, setVerifyTab] = useState('phone');
  const [verifyCode, setVerifyCode] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const isModify = Boolean(member?.hasJyPassword);

  const hasEmail = Boolean(member?.email?.trim());
  const hasMobile = Boolean(member?.mobile?.trim());

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (hasMobile) {
      tabs.push('phone');
    }
    if (hasEmail) {
      tabs.push('email');
    }
    return tabs;
  }, [hasEmail, hasMobile]);

  useEffect(() => {
    let cancelled = false;
    memberApi
      .getInfo()
      .then((info) => {
        if (cancelled) {
          return;
        }
        setMember(info || null);
        if (info?.mobile?.trim()) {
          setVerifyTab('phone');
        } else if (info?.email?.trim()) {
          setVerifyTab('email');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMember(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSendCode() {
    setError('');
    setSendingCode(true);
    try {
      if (verifyTab === 'email') {
        if (!member?.email?.trim()) {
          throw new ApiError(t('security.emailMissing'));
        }
        await memberApi.sendVerifyCodeEmail({
          scene: VERIFY_SCENE,
          email: member.email.trim(),
        });
      } else {
        if (!member?.mobile?.trim()) {
          throw new ApiError(t('security.phoneMissing'));
        }
        await memberApi.sendVerifyCodePhone({
          scene: VERIFY_SCENE,
          countryEnName: member.country || 'TW',
          phoneAreaCode: member.areaCode || '+886',
          phoneNumber: member.mobile.trim(),
        });
      }
      setCodeSent(verifyTab);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('security.codeSendFailed'));
    } finally {
      setSendingCode(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');

    if (!newPassword || newPassword.length < 6 || newPassword.length > 12 || /\s/.test(newPassword)) {
      setError(t('security.passwordFormat'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('security.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        resetType: 'SAFE',
        newPassword,
        verifyCode: verifyCode.trim() || undefined,
        oldPassword: isModify ? oldPassword : undefined,
        email: verifyTab === 'email' ? member?.email?.trim() : undefined,
        phoneNumber: verifyTab === 'phone' ? member?.mobile?.trim() : undefined,
        countryEnName: verifyTab === 'phone' ? (member?.country || 'TW') : undefined,
      };
      await memberApi.setPassword(payload);
      const redirectTo = location.state?.from || '/security';
      navigate(redirectTo, { replace: true, state: { passwordUpdated: 'withdraw' } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('security.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  const pageTitle = isModify
    ? t('security.withdrawPasswordModifyTitle')
    : t('security.withdrawPasswordSetTitle');

  return (
    <SettingsSubPageShell title={pageTitle}>
      {loading && <p className="security-page__status">{t('common.loading')}</p>}

      {!loading && (
        <form className="security-password-form" onSubmit={handleSubmit}>
          {availableTabs.length > 1 && (
            <div className="security-password-form__tabs">
              {hasMobile && (
                <button
                  type="button"
                  className={verifyTab === 'phone' ? 'is-active' : ''}
                  onClick={() => setVerifyTab('phone')}
                >
                  {t('security.tabPhone')}
                </button>
              )}
              {hasEmail && (
                <button
                  type="button"
                  className={verifyTab === 'email' ? 'is-active' : ''}
                  onClick={() => setVerifyTab('email')}
                >
                  {t('security.tabEmail')}
                </button>
              )}
            </div>
          )}

          {verifyTab === 'phone' && hasMobile && (
            <div className="security-form-field">
              <label className="security-form-field__label">{t('security.phoneNumber')}</label>
              <div className="security-form-field__readonly">
                <span className="security-form-field__prefix">{member?.areaCode || '+886'}</span>
                <span>{member.mobile}</span>
              </div>
            </div>
          )}

          {verifyTab === 'email' && hasEmail && (
            <div className="security-form-field">
              <label className="security-form-field__label">{t('security.email')}</label>
              <div className="security-form-field__readonly">{member.email}</div>
            </div>
          )}

          {availableTabs.length > 0 && (
            <div className="security-form-field">
              <label className="security-form-field__label" htmlFor="withdraw-verify-code">
                {t('security.verifyCode')}
              </label>
              <div className="security-form-field__code-row">
                <input
                  id="withdraw-verify-code"
                  className="security-form-field__control"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  placeholder={t('security.verifyCodePlaceholder')}
                />
                <button
                  type="button"
                  className="security-form-field__send-btn"
                  disabled={sendingCode}
                  onClick={handleSendCode}
                >
                  {sendingCode ? t('common.loading') : t('security.sendCode')}
                </button>
              </div>
              {codeSent && <p className="security-form-field__hint">{t(codeSent === 'email' ? 'emailVerification.accepted' : 'security.codeSent')}</p>}
            </div>
          )}

          {isModify && (
            <PasswordField
              id="withdraw-old-password"
              label={t('security.oldPassword')}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder={t('security.oldPasswordPlaceholder')}
              autoComplete="current-password"
            />
          )}

          <PasswordField
            id="withdraw-new-password"
            label={t('security.newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t('security.newPasswordPlaceholder')}
          />

          <PasswordField
            id="withdraw-confirm-password"
            label={t('security.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('security.confirmPasswordPlaceholder')}
          />

          {error && <p className="security-page__error">{error}</p>}

          <button
            type="submit"
            className="security-password-form__submit"
            disabled={submitting}
          >
            {submitting ? t('common.loading') : (isModify ? t('security.modify') : t('security.set'))}
          </button>
        </form>
      )}
    </SettingsSubPageShell>
  );
}
